import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit, MoreHorizontal, Ticket } from 'lucide-react';

import { ticketService } from '@services/ticketService';
import { useAuth } from '@context/AuthContext';
import TicketDetails from '@components/tickets/TicketDetails';
import Button, { IconButton } from '@components/common/Button';
import Loading from '@components/common/Loading';

const TicketDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  useEffect(() => {
    document.title = `Ticket #${id} - RDX Ticketing System`;
  }, [id]);

  // Fetch ticket details
  const {
    data: ticket,
    isLoading,
    error
  } = useQuery(
    ['ticket', id],
    async () => {
      return await ticketService.getTicket(id);
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      staleTime: 10000,
      retry: 2,
      enabled: !!id
    }
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.32,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  if (error) {
    return (
      <motion.div
        className="min-h-[60vh] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ticket Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The ticket you're looking for doesn't exist or you don't have
            permission to view it.
          </p>
          <Link to="/tickets">
            <Button variant="primary">Back to Tickets</Button>
          </Link>
        </div>
      </motion.div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
          <div className="space-y-2">
            <div className="w-48 h-6 bg-gray-200 rounded animate-pulse" />
            <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <Loading size="lg" text="Loading ticket details..." />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="flex flex-col gap-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Back Button */}
      <motion.div variants={itemVariants} className="mb-4">
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </motion.div>

      {/* Ticket Details Hero */}
      <motion.div variants={itemVariants}>
        <div className="rounded-2xl p-5 text-white overflow-hidden shadow-xl bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl font-bold m-0 text-white leading-tight flex items-center gap-2">
                <Ticket className="w-6 h-6 inline-block" />
                {ticket.ticket_number}
              </h1>
              <p className="mt-1.5 text-sm md:text-base text-blue-50">
                {ticket.subject}
              </p>
            </div>

            <div className="text-right min-w-44 hidden sm:block">
              <p className="m-0 text-sm md:text-base text-blue-50">
                Created {new Date(ticket.created_at).toLocaleDateString('en-IN', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
              <div className="flex gap-2 mt-2">
                {(hasPermission('can_edit') || user?.role === 'master' || user?.role === 'admin') && (
                  <Link to={`/tickets/${id}/edit`}>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Edit className="w-3.5 h-3.5" />}
                    >
                      Edit
                    </Button>
                  </Link>
                )}
                {(user?.role === 'master' || user?.role === 'admin') && (
                  <IconButton variant="secondary" size="sm" tooltip="More options">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </IconButton>
                )}
              </div>
            </div>
                        
            {/* Mobile Action Menu */}
            <div className="sm:hidden w-full mt-4">
              <div className="flex gap-2">
                {(hasPermission('can_edit') || user?.role === 'master' || user?.role === 'admin') && (
                  <Link to={`/tickets/${id}/edit`}>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Edit className="w-3.5 h-3.5" />}
                      className="flex-1"
                    >
                      Edit
                    </Button>
                  </Link>
                )}
                {(user?.role === 'master' || user?.role === 'admin') && (
                  <IconButton variant="secondary" size="sm" tooltip="More options">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </IconButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Ticket Details Content */}
      <motion.div variants={itemVariants}>
        <div className="bg-transparent">
          <TicketDetails ticket={ticket} />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TicketDetailsPage;
